import React, { useState } from "react";
import {
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBModalFooter,
  MDBBtn,
} from "mdbreact";
import { useDispatch } from "react-redux";

import Loading from "../ui-components/loading";
import { useHttp } from "hooks/useHttp";
import { choseToggle } from "helpers/toggleIfLoading";
import addNotification from "helpers/notify";
import { mainAdminSetNewDateForCompany } from "actions/main-admin";
import { mainAdminSuspendCompany } from "api/users/mainAdminSuspendCompany";
import { mainAdminResumeCompany } from "api/users/mainAdminResumeCompany";

// Suspend / resume a customer from the All Companies table.
//
// Suspend: Company.enabled=false (every login refused, open sessions cut
// off on their next request) + Stripe pause_collection on the company's
// subscription(s). Resume undoes both. The billing half is best-effort:
// the server reports billing.error when Stripe could not be reached and
// the admin is told to finish that part in the Stripe dashboard.
const CompanySuspendModal = (props) => {
  const { company, isOpen } = props;
  const suspended = !!company && company.enabled === false;
  const [reason, setReason] = useState("");
  const [touchBilling, setTouchBilling] = useState(true);
  const dispatch = useDispatch();

  const toggle = () => {
    props.toggle();
    setReason("");
    setTouchBilling(true);
  };

  const describeBilling = (billing, verb) => {
    if (!billing) return "";
    if (billing.error) {
      return ` Stripe could not be updated (${billing.error}) - ${verb} the subscription by hand in the Stripe dashboard.`;
    }
    const ids = billing.paused || billing.resumed || [];
    if (ids.length) return ` Stripe billing ${verb}d on ${ids.length} subscription${ids.length > 1 ? "s" : ""}.`;
    if (billing.customerId) return " No billable Stripe subscription was found.";
    return " This company has no Stripe customer.";
  };

  const { request, loading } = useHttp({
    requestCallback: (data) =>
      suspended
        ? mainAdminResumeCompany(company._id, data)
        : mainAdminSuspendCompany(company._id, data),
    onLoad: (res) => {
      // The route returns the bare company doc; keep the row's plan and
      // counts by merging over what the table already holds.
      dispatch(mainAdminSetNewDateForCompany({ ...company, ...res.company }));
      toggle();
      const verb = suspended ? "resume" : "pause";
      addNotification(
        `${company.companyName} ${suspended ? "resumed" : "suspended"}.` +
          describeBilling(res.billing, verb),
        res.billing && res.billing.error ? "warning" : "success"
      );
    },
    onError: (err) => {
      addNotification(
        (err && err.err) || "Something went wrong, please contact support",
        "danger"
      );
    },
  });

  const submit = () => {
    if (suspended) request({ resumeBilling: touchBilling });
    else request({ reason: reason.trim(), pauseBilling: touchBilling });
  };

  if (!company) return null;

  return (
    <MDBModal
      isOpen={isOpen}
      toggle={choseToggle(loading, toggle)}
      centered
      className="expiration-modal"
    >
      <MDBModalHeader toggle={choseToggle(loading, toggle)}>
        {suspended ? "Resume" : "Suspend"} {company.companyName}
      </MDBModalHeader>
      <MDBModalBody>
        {suspended ? (
          <>
            <p className="mb-2">
              Logins for this company will work again immediately.
            </p>
            {company.suspendReason ? (
              <p className="text-muted f-13 mb-2">
                Suspended{company.suspendedAt ? ` on ${new Date(company.suspendedAt).toLocaleDateString()}` : ""}
                {company.suspendSource === "stripe" ? " by a Stripe pause" : ""}: {company.suspendReason}
              </p>
            ) : null}
          </>
        ) : (
          <>
            <p className="mb-2">
              Nobody in this company will be able to log in, and anyone
              already signed in is cut off on their next request.
            </p>
            <label className="f-13 mb-1" htmlFor="suspend-reason">
              Reason (shown to admins only)
            </label>
            <textarea
              id="suspend-reason"
              className="form-control mb-3"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. unpaid invoice, requested by customer"
              disabled={loading}
            />
          </>
        )}
        <div className="custom-control custom-checkbox">
          <input
            type="checkbox"
            className="custom-control-input"
            id="suspend-billing"
            checked={touchBilling}
            onChange={(e) => setTouchBilling(e.target.checked)}
            disabled={loading}
          />
          <label className="custom-control-label f-13" htmlFor="suspend-billing">
            {suspended
              ? "Resume Stripe billing on the paused subscription"
              : "Pause Stripe billing (no invoices are collected while suspended)"}
          </label>
        </div>
        {loading ? (
          <Loading color="dark" text={suspended ? "Resuming..." : "Suspending..."} />
        ) : null}
      </MDBModalBody>
      <MDBModalFooter className="d-flex justify-content-center border-none ">
        <MDBBtn
          color=""
          size="sm"
          className={`${suspended ? "bg-bid" : "bg-danger"} text-white card-button text-inherit w-100 expiration-btn`}
          disabled={loading}
          onClick={submit}
        >
          {suspended ? "Resume company" : "Suspend company"}
        </MDBBtn>
      </MDBModalFooter>
    </MDBModal>
  );
};

export default CompanySuspendModal;
